/**
 * Section constants for Allergies v2. Allergens themselves come from
 * api-allergy-v2-service's federated catalog (salts from api-medicine-service
 * /medicine/salts + own foods/other collection). This file holds the enums
 * used by the modal and table (category dropdown, reaction quick-picks) and
 * a small display helper.
 */

/**
 * Category enum — matches backend constants.Category*. Drug allergies are
 * SALT-level (patient reacts to the active ingredient, not the brand), so
 * the picker's drug side is a salt search and the category on the record
 * is "Salt".
 */
export const CATEGORIES = [
  { key: "Salt", label: "Salt" },
  { key: "Food", label: "Food" },
  { key: "Others", label: "Others" },
];

/**
 * Curated reaction quick-picks shown as suggested pills in the Reactions
 * editor. Free-text is also accepted — the picker distinguishes with
 * source="controlled" vs "free-text" on the wire.
 */
export const REACTION_SUGGESTIONS = [
  "Hives",
  "Rash",
  "Swelling",
  "Angioedema",
  "Nausea",
  "Vomiting",
  "Breathing difficulty",
  "Anaphylaxis",
  "Itching",
  "Maculopapular rash",
  "Contact dermatitis",
  "Lip swelling",
  "Throat swelling",
];

/** Lower-cased suggestion set — reactions in it render as SUGGESTED chips,
 *  everything else as free-text tokens inside the token box. */
export const SUGGESTION_KEYS = new Set(REACTION_SUGGESTIONS.map((s) => s.toLowerCase()));

/** Case-insensitive add of a reaction pill; no-op on duplicates or blanks.
 *  Shared by the add rail (AllergyEditor) and the update dialog. Reaction
 *  entries carry only the display name — the earlier `source` field was
 *  retired in 2026-09 after the frontend locked reactions to the catalog
 *  dropdown. */
export const upsertReaction = (list, name) => {
  const clean = (name || "").trim();
  if (!clean) return list;
  if (list.some((r) => r.name.toLowerCase() === clean.toLowerCase())) return list;
  return [...list, { name: clean }];
};

/** Case-insensitive removal of a reaction pill by name. */
export const removeReaction = (list, name) =>
  list.filter((r) => r.name.toLowerCase() !== (name || "").toLowerCase());
